import { Center, Stack, VStack } from "@chakra-ui/react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BiLockOpenAlt } from "react-icons/bi";
import { RiAccountCircleLine } from "react-icons/ri";
import {
  validateEmail,
  validatePassword,
  validateRequired,
} from "../../services/validation";
import { useUserContext } from "../../Contexts/userContext";
import FormContainer from "../CustomUi/FormContainer";
import FormNavigationLinks from "../CustomUi/FormNavigationLinks";
import TextInput from "../CustomUi/TextInput";
import CustomButton from "../CustomUi/CustomButton";
import CustomPasswordInput from "../CustomUi/CustomPasswordInput";
import useAuth from "../../hooks/useAuth";
import { LoginRequestDto } from "../../services/Dtos";
import { useFormChanges } from "../../hooks/useFormChanges";
import { useFormFieldHandler } from "../../hooks/useFormFieldHandler";
import CustomAlert from "../CustomUi/CustomAlert";
import useThemeColors from "../../hooks/useThemeColors";

function LoginForm() {
  const { isLoading } = useUserContext();
  const [loginData, setLoginData] = useState<LoginRequestDto>({
    username: "",
    password: "",
  });
  const { login, loading, error } = useAuth();
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const colors = useThemeColors();
  const navigate = useNavigate();

  // Define field configurations for form validation
  const fieldConfigs = {
    username: { type: "email" as const, required: true },
    password: { type: "text" as const, required: true },
  };

  // Use the form changes hook
  const { hasChanges, validateField, validateAllFields, hasValidationErrors } =
    useFormChanges({
      fields: fieldConfigs,
      initialData: null,
      currentData: loginData,
      isEditMode: false,
    });

  // Use the form field handler hook
  const { handleFieldChange, handleFieldBlur } = useFormFieldHandler({
    setFormState: setLoginData,
    setTouchedFields,
  });

  // Custom validation for email
  const validateEmailField = (value: string): string => {
    if (!validateRequired(value)) {
      return "Email is required";
    }
    if (!validateEmail(value)) {
      return "Valid email is required";
    }
    return "";
  };

  // Custom validation for password
  const validatePasswordField = (value: string): string => {
    if (!validateRequired(value)) {
      return "Password is required";
    }
    if (!validatePassword(value)) {
      return "Password must be at least 8 characters and contain at least one uppercase letter, one lowercase letter, one number and one special character";
    }
    return "";
  };

  // Real-time validation using the hook's validateField
  const validateFieldByName = (name: string, value: any): string => {
    const fieldKey = name as keyof LoginRequestDto;

    // Custom validation for specific fields
    if (name === "username") {
      return validateEmailField(value);
    }
    if (name === "password") {
      return validatePasswordField(value);
    }

    return validateField(fieldKey, value);
  };

  const showFieldError = (fieldName: string): boolean => {
    const error = validateFieldByName(
      fieldName,
      loginData[fieldName as keyof LoginRequestDto]
    );
    const shouldShow = !!(error && touchedFields.has(fieldName));
    return shouldShow;
  };

  const getFieldError = (fieldName: string): string => {
    return validateFieldByName(
      fieldName,
      loginData[fieldName as keyof LoginRequestDto]
    );
  };

  const currentValidationErrors = validateAllFields();
  const hasValidationErrorsValue = hasValidationErrors(currentValidationErrors);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if form has changes
    if (!hasChanges) {
      return;
    }

    // Validate all fields
    const errors = validateAllFields();
    if (hasValidationErrors(errors)) {
      return;
    }

    await login(loginData);
  };

  return (
    <Center h={"full"} w={"full"}>
      <FormContainer
        heading="Login"
        description="Welcome back! Please enter your details."
      >
        {error.length > 0 && (
          <VStack gap={2} mb={4} align={"stretch"}>
            {error.slice(0, 3).map((errorMessage, index) => (
              <CustomAlert key={index} message={errorMessage} tight />
            ))}
          </VStack>
        )}

        <form onSubmit={handleLogin} style={{ width: "100%" }}>
          <Stack gap={4} w={"full"}>
            <TextInput
              invalid={showFieldError("username")}
              required
              label="Email"
              type="email"
              name="username"
              value={loginData.username}
              onChange={handleFieldChange}
              onBlur={() => handleFieldBlur("username")}
              error={
                showFieldError("username") ? getFieldError("username") : ""
              }
            />
            <CustomPasswordInput
              invalid={showFieldError("password")}
              required
              name="password"
              value={loginData.password}
              onChange={handleFieldChange}
              onBlur={() => handleFieldBlur("password")}
              error={
                showFieldError("password") ? getFieldError("password") : ""
              }
            />

            <CustomButton
              label="Login"
              disabled={isLoading || !hasChanges || hasValidationErrorsValue}
              loading={loading}
              w={"full"}
              type="submit"
              colorPalette={colors.brand.primary}
            />
          </Stack>
        </form>

        <FormNavigationLinks
          links={[
            {
              text: "Create Account",
              icon: RiAccountCircleLine,
              isDisabled: loading,
              onClick: () => navigate("/auth/register"),
            },
            {
              text: "Forgot Password?",
              icon: BiLockOpenAlt,
              isDisabled: loading,
              onClick: () => navigate("/auth/forgotpassword"),
            },
          ]}
        />
      </FormContainer>
    </Center>
  );
}

export default LoginForm;
